import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { type CSSProperties, useRef } from "react";
import sectionFourBkg from "../../assets/section-four-bkg.png?inline";
import { INTERACTIVE_FOCUS } from "../../lib/chrome";
import { useLandingCopy } from "../../lib/copy";
import { GrainLowOverlay } from "../../lib/grain-low-overlay";
import { LANDING_CONTAINER_CLASS } from "../../lib/layout";
import { useLandingPartnerLogos } from "../../lib/partner-logos";
import { PARTNER_LOGO_CAPTION_CLASS, PARTNER_LOGO_IMAGE_CLASS, PARTNER_LOGO_ROW_CLASS } from "./partner-logo-layout";
import { SectionFourCtaCard } from "./section-four-cta-card";
import { StatCountUpValue } from "./stat-count-up";
import { useSectionFourOverlapUnits } from "./use-section-four-overlap";
import { SECTION_FOUR_PARALLAX_BLEED_PERCENT, useSectionFourParallax } from "./use-section-four-parallax";
import "./partner-logos.css";

type TStatBlock = {
	value: string;
	label: string;
	description: string;
};

const STAT_BLOCKS: readonly TStatBlock[] = [
	{
		value: "+126%",
		label: "Net sales increase*",
		description: "Never run out of stock & double-down on your best sellers.",
	},
	{
		value: "+59%",
		label: "New customer orders*",
		description: "Free up cash for marketing to acquire more new customers.",
	},
	{
		value: "+37%",
		label: "Returning customer orders*",
		description: "Get fully stocked for retention programmes and increasing customer LTV.",
	},
];

/** One frosted-glass stat card (Figma 310×300, scales up to ~400×387 on xl): number top-left, label + description pinned bottom-left. */
function StatBlock({ block, index }: { block: TStatBlock; index: number }) {
	return (
		<div className="landing-stat-glass flex w-full flex-col overflow-visible px-8 py-8 text-left text-white backdrop-blur-[18px] backdrop-saturate-[1.4] md:px-10 md:py-10 xl:aspect-[31/30] xl:px-10 xl:py-9">
			<p className="m-0 shrink-0 text-[clamp(34px,3.8vw,56px)] font-light leading-[1.2] tracking-[-0.03em] [text-shadow:0_2px_15px_rgba(0,0,0,0.15)]">
				<StatCountUpValue value={block.value} delay={index * 0.12} />
			</p>
			<div className="mt-auto flex shrink-0 flex-col gap-2 pt-14 xl:gap-2.5 xl:pt-12">
				<p className="m-0 shrink-0 text-lg font-semibold leading-[1.2] tracking-[-0.03em] [text-shadow:0_3px_20px_rgba(0,0,0,0.2)] lg:text-xl">
					{block.label}
				</p>
				<p className="m-0 shrink-0 text-[13px] font-normal leading-[1.2] tracking-[-0.03em] [text-shadow:0_3px_20px_rgba(0,0,0,0.2)] lg:text-sm">
					{block.description}
				</p>
			</div>
		</div>
	);
}

/**
 * Section 4 — performance stats over the desk photo, then the testimonial +
 * partner-logo block. Slides over the pinned S3 world.
 */
export function SectionFour({ className }: { className?: string }) {
	const { cta } = useLandingCopy();
	const partnerLogos = useLandingPartnerLogos();
	const { clipRef, imageY } = useSectionFourParallax();
	const sectionRef = useRef<HTMLElement | null>(null);
	const overlapUnits = useSectionFourOverlapUnits(sectionRef);

	return (
		<section
			ref={sectionRef}
			id="triffin-section-four"
			className={cn("landing-section-four-cover relative z-50 w-full", className)}
			style={{ "--section-four-overlap": overlapUnits } as CSSProperties}
			aria-label={cta.heading}
		>
			{/* Stats panel: greige-400 + grain paper with the desk photo inset — 40px on tablet (md), 80px on xl+. */}
			<div className="relative flex min-h-[100svh] flex-col bg-greige-400 p-0 supports-[height:100dvh]:min-h-[100dvh] md:p-10 xl:p-20">
				<GrainLowOverlay />
				<div ref={clipRef} className="relative z-[1] flex flex-1 flex-col overflow-hidden">
					<motion.div
						aria-hidden="true"
						className="pointer-events-none absolute z-0 select-none will-change-transform"
						style={{ inset: `-${SECTION_FOUR_PARALLAX_BLEED_PERCENT}%`, y: imageY }}
					>
						<img src={sectionFourBkg} alt="" className="h-full w-full object-cover" />
					</motion.div>
					<div aria-hidden="true" className="pointer-events-none absolute inset-0 z-[1] bg-black/20" />
					<div className="relative z-[2] grid flex-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] px-8 text-center md:px-10 xl:px-12">
						<div className="flex min-h-0 min-w-0 flex-col justify-end overflow-hidden px-10 pt-12 pb-9 md:pb-[52px]">
							<h2 className="m-0 mx-auto min-w-0 max-w-[640px] text-[clamp(26px,5vw,40px)] font-normal text-white [text-shadow:0_1px_16px_rgba(54,47,44,0.18)]">
								Brands using Triffin perform better
							</h2>
							<p className="m-0 mx-auto mt-3 min-w-0 max-w-[520px] text-center text-base font-normal text-white/90 [text-shadow:0_1px_16px_rgba(54,47,44,0.18)]">
								Triffin pays your supplier, you pay us back in up to six months
							</p>
						</div>
						<div className="flex min-w-0 flex-col items-center">
							<div className="grid w-full min-w-0 grid-cols-1 gap-4 xl:grid-cols-[repeat(3,minmax(320px,400px))] xl:justify-center">
								{STAT_BLOCKS.map((block, index) => (
									<StatBlock key={block.label} block={block} index={index} />
								))}
							</div>
						</div>
						<div className="flex min-h-0 min-w-0 flex-col justify-end overflow-hidden pb-8 md:px-2 md:pb-12">
							<p className="m-0 mx-auto w-max max-w-[calc(100%-3rem)] text-center text-[13px] font-normal leading-[1.4] tracking-[-0.01em] text-white [text-shadow:0_1px_16px_rgba(54,47,44,0.18)] md:max-w-[calc(100%-4rem)]">
								*6 months average performance improvement for Triffin Credit customers
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Testimonial + CTA + partner logos on the greige-400 + grain paper background. */}
			<div className="relative bg-greige-400 px-6 pt-24 pb-[120px] text-teal-500 md:px-10 md:pb-[108px] md:pt-[108px]">
				<GrainLowOverlay />
				<div className={cn(LANDING_CONTAINER_CLASS, "relative z-[1] flex flex-col items-center gap-32 md:gap-40")}>
					<div className="mx-auto flex w-full max-w-[1100px] flex-col items-center gap-10">
						<p className={PARTNER_LOGO_CAPTION_CLASS}>{cta.partnerLogoCaption}</p>
						<ul className={PARTNER_LOGO_ROW_CLASS} data-landing-partner-logos>
							{partnerLogos.map((logo) => {
								const image = (
									<img alt={logo.href ? "" : logo.name} className={PARTNER_LOGO_IMAGE_CLASS} src={logo.src} />
								);
								return (
									<li key={`${logo.name}-${logo.src}`} data-landing-partner-logo>
										{logo.href ? (
											<a
												aria-label={logo.name}
												className={cn("inline-flex max-w-full items-center no-underline", INTERACTIVE_FOCUS)}
												href={logo.href}
											>
												{image}
											</a>
										) : (
											image
										)}
									</li>
								);
							})}
						</ul>
					</div>

					<SectionFourCtaCard />
				</div>
			</div>
		</section>
	);
}
